import { useQuery } from "@apollo/client";
import { Children, isValidElement, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { useLocation } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { TFLocationState } from "types/topicflow";

import {
  canOpenInSidebar,
  getInternalPathname,
} from "@apps/main/routes-constants";
import Avatar from "@components/avatar/avatar";
import getUserQuery from "@graphql/get-user-query";
import { classNames } from "@helpers/css";
import { toWithBackground } from "@helpers/helpers";

import AskAiArtifact from "./ask-ai-artifact";
import {
  askAiEmbedContainerClassName,
  askAiEmbedItemClassName,
} from "./ask-ai-constants";
import AskAILink from "./ask-ai-link";
import AskAIMeetingCard from "./ask-ai-meeting-card";

// Punctuation characters that can trail a standalone link without
// preventing card rendering (includes ASCII and Unicode dashes).
const TRAILING_PUNCTUATION_RE = /^[.,!?;:()[\]\s\-–—]+|[.,!?;:()[\]\s\-–—]+$/g;

// The AI sometimes generates markdown links with a space between ] and (
// e.g. [Title] (https://...) instead of [Title](https://...).
// Standard markdown parsers don't recognize that as a link.
const normalizeMarkdownLinks = (md: string): string =>
  md.replace(/(\[[^\]]+\])\s+(\([^)]+\))/g, "$1$2");

const removeProfileHeadingDividers = (md: string): string =>
  md.replace(
    /^([ \t]*[-*_]{3,}[ \t]*\n)([ \t]*\n)*(#{1,6}\s+\[[^\]]+\]\((?:https?:\/\/[^)\s]+)?\/profile\/\d+\/?\)[^\n]*$)/gm,
    "$3",
  );

const normalizeAskAIMarkdown = (md: string): string =>
  removeProfileHeadingDividers(normalizeMarkdownLinks(md));

const headingClassNames = {
  h1: "text-base font-bold my-2",
  h2: "text-[15px] font-bold my-2",
  h3: "text-sm font-bold my-1.5",
  h4: "text-sm font-bold my-1.5",
  h5: "text-sm font-bold my-1.5",
  h6: "text-sm font-bold my-1.5",
};

const getTextFromMarkdownNode = (node: any): string =>
  (node?.children || [])
    .map((child: any) =>
      child?.type === "text" ? child.value : getTextFromMarkdownNode(child),
    )
    .join("");

const profileHeadingText = (linkText: string, trailingText = ""): string => {
  const trimmedTrailingText = trailingText.trim();
  return [linkText.trim(), trimmedTrailingText].filter(Boolean).join(" ");
};

const getProfileHeadingTrailingTextFromHastNode = (
  node: any,
): string | null => {
  const childTexts: Array<string | null> = (node?.children || []).map(
    (child: any) => {
      if (child?.type === "text") return child.value || "";
      if (
        child?.tagName === "a" &&
        child.properties?.href?.startsWith("mailto:")
      ) {
        return getTextFromMarkdownNode(child);
      }
      return null;
    },
  );

  return childTexts.reduce<string | null>(
    (text, childText) =>
      text === null || childText === null ? null : text + childText,
    "",
  );
};

const getProfileHeader = (
  node: any,
): { userId: number; name: string } | null => {
  const children = node?.children || [];
  if (!children.length || children[0]?.tagName !== "a") return null;

  const href = children[0].properties?.href;
  if (!href) return null;

  const internalPathname = getInternalPathname(href) || href;
  const match = internalPathname.match(/^\/profile\/(\d+)\/?$/);
  if (!match) return null;

  const trailingText = getProfileHeadingTrailingTextFromHastNode({
    children: children.slice(1),
  });
  if (trailingText === null) return null;

  const name = profileHeadingText(
    getTextFromMarkdownNode(children[0]),
    trailingText,
  );
  if (!name) return null;

  return { userId: Number(match[1]), name };
};

const TOPICFLOW_CARD_PATTERNS: {
  type: "artifact" | "meeting";
  pattern: RegExp;
}[] = [
  { type: "artifact", pattern: /\/goal\/(\d+)/ },
  { type: "artifact", pattern: /\/action-item\/(\d+)/ },
  { type: "artifact", pattern: /\/feedback\/(\d+)/ },
  { type: "artifact", pattern: /\/recognition\/(\d+)/ },
  { type: "meeting", pattern: /\/meeting\/\d+\/(\d+)/ },
];

const getCardFromHref = (
  href: string,
): { type: "artifact" | "meeting"; id: number } | null => {
  const pathname = getInternalPathname(href) || href;
  for (const { type, pattern } of TOPICFLOW_CARD_PATTERNS) {
    const match = pathname.match(pattern);
    if (match) return { type, id: Number(match[1]) };
  }
  return null;
};

// react-markdown v10 passes hast (HTML AST) nodes: tagName instead of
// mdast type, properties.href instead of url.
const getStandaloneCardFromHastNode = (
  node: any,
): { type: "artifact" | "meeting"; id: number } | null => {
  let children = node?.children;
  if (!Array.isArray(children)) return null;
  if (children.length === 1 && children[0]?.tagName === "p") {
    children = children[0].children;
    if (!Array.isArray(children)) return null;
  }
  const links = children.filter((c: any) => c?.tagName === "a");
  if (links.length !== 1) return null;
  const hasExtraContent = children.some((c: any) => {
    if (c?.tagName === "a") return false;
    if (c?.type === "text") {
      return (
        (c.value || "").trim().replace(TRAILING_PUNCTUATION_RE, "").length > 0
      );
    }
    return true;
  });
  if (hasExtraContent) return null;
  return getCardFromHref(links[0].properties?.href || "");
};

const getStandaloneCardFromChildren = (
  children: React.ReactNode,
): { type: "artifact" | "meeting"; id: number } | null => {
  const childArray = Children.toArray(children);
  if (childArray.length !== 1 || !isValidElement(childArray[0])) return null;
  const href = (childArray[0].props as any).href;
  if (!href) return null;
  return getCardFromHref(href);
};

const AskAIHeading = ({
  children,
  level,
  node,
  ...props
}: {
  children?: React.ReactNode;
  level: keyof typeof headingClassNames;
  node?: any;
  [key: string]: any;
}) => {
  const location = useLocation<TFLocationState>();
  const profileHeader = getProfileHeader(node);
  const { data } = useQuery(getUserQuery, {
    fetchPolicy: "cache-first",
    skip: !profileHeader?.userId,
    variables: { userId: profileHeader?.userId },
  });

  if (profileHeader) {
    const user = data?.user || {
      id: profileHeader.userId,
      name: profileHeader.name,
      avatar: null,
    };

    return (
      <p
        className={classNames(
          headingClassNames[level],
          "flex items-center gap-1.5",
        )}
        {...props}
      >
        <Avatar user={user} size={5} showTooltip={false} />
        <AskAILink
          to={toWithBackground({
            pathname: `/profile/${profileHeader.userId}`,
            location,
          })}
          className="no-underline hover:underline"
        >
          {profileHeader.name}
        </AskAILink>
      </p>
    );
  }

  return (
    <p className={headingClassNames[level]} {...props}>
      {children}
    </p>
  );
};

const renderCard = (card: { type: "artifact" | "meeting"; id: number }) => {
  const key = `${card.type}-${card.id}`;
  return (
    <div key={key} className={askAiEmbedItemClassName}>
      {card.type === "meeting" ? (
        <AskAIMeetingCard id={card.id} />
      ) : (
        <AskAiArtifact id={card.id} />
      )}
    </div>
  );
};

const renderListWithCardGroups = (
  astItems: any[],
  children: React.ReactNode,
  ListTag: "ul" | "ol",
  listProps: any,
) => {
  const childArray = Children.toArray(children).filter(isValidElement);
  const groups: {
    type: "cards" | "items";
    elements: React.ReactNode[];
  }[] = [];

  astItems.forEach((astItem: any, i: number) => {
    const card = getStandaloneCardFromHastNode(astItem);
    const lastGroup = groups[groups.length - 1];
    if (card) {
      if (lastGroup?.type === "cards") {
        lastGroup.elements.push(renderCard(card));
      } else {
        groups.push({ type: "cards", elements: [renderCard(card)] });
      }
    } else {
      const renderedChild = childArray[i] || null;
      if (lastGroup?.type === "items") {
        lastGroup.elements.push(renderedChild);
      } else {
        groups.push({ type: "items", elements: [renderedChild] });
      }
    }
  });

  return (
    <>
      {groups.map((group, i) =>
        group.type === "cards" ? (
          <div key={i} className={askAiEmbedContainerClassName}>
            {group.elements}
          </div>
        ) : (
          <ListTag key={i} {...listProps}>
            {group.elements}
          </ListTag>
        ),
      )}
    </>
  );
};

// Module-level component overrides keep stable references across renders,
// preventing React from unmounting/remounting children (e.g. artifact cards)
// when ReactMarkdown re-renders during streaming.

const MarkdownH1 = (props: any) => <AskAIHeading level="h1" {...props} />;
const MarkdownH2 = (props: any) => <AskAIHeading level="h2" {...props} />;
const MarkdownH3 = (props: any) => <AskAIHeading level="h3" {...props} />;
const MarkdownH4 = (props: any) => <AskAIHeading level="h4" {...props} />;
const MarkdownH5 = (props: any) => <AskAIHeading level="h5" {...props} />;
const MarkdownH6 = (props: any) => <AskAIHeading level="h6" {...props} />;

const ALLOWED_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);
const linkClassName =
  "font-medium no-underline border-b border-gray-300 hover:border-gray-500";

const MarkdownLink = ({
  href,
  children,
  node: _node,
  className,
  ...props
}: any) => {
  const location = useLocation<TFLocationState>();
  if (!href) return null;
  let url: URL | null = null;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return <span {...props}>{children}</span>;
  }
  if (!ALLOWED_PROTOCOLS.has(url.protocol)) {
    return <span {...props}>{children}</span>;
  }
  const internalPathname = getInternalPathname(href);
  const isSidebarUrl = canOpenInSidebar(href);
  if (internalPathname) {
    return (
      <AskAILink
        to={
          isSidebarUrl
            ? toWithBackground({ pathname: internalPathname, location })
            : internalPathname
        }
        className={classNames(linkClassName, className)}
        {...props}
      >
        {children}
      </AskAILink>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={classNames(linkClassName, className)}
      {...props}
    >
      {children}
    </a>
  );
};

const MarkdownUl = ({ children, node, ...props }: any) => {
  const astItems = (node?.children || []).filter(
    (c: any) => c?.tagName === "li",
  );
  const hasCards = astItems.some(
    (li: any) => getStandaloneCardFromHastNode(li) !== null,
  );
  if (!hasCards) return <ul {...props}>{children}</ul>;
  return renderListWithCardGroups(astItems, children, "ul", props);
};

const MarkdownOl = ({ children, node, ...props }: any) => {
  const astItems = (node?.children || []).filter(
    (c: any) => c?.tagName === "li",
  );
  const hasCards = astItems.some(
    (li: any) => getStandaloneCardFromHastNode(li) !== null,
  );
  if (!hasCards) return <ol {...props}>{children}</ol>;
  return renderListWithCardGroups(astItems, children, "ol", props);
};

const MarkdownLi = ({ children, node, ...props }: any) => {
  const card =
    getStandaloneCardFromHastNode(node) ||
    getStandaloneCardFromChildren(children);
  if (card) return renderCard(card);
  return <li {...props}>{children}</li>;
};

const MarkdownP = ({ children, node, ...props }: any) => {
  const card =
    getStandaloneCardFromHastNode(node) ||
    getStandaloneCardFromChildren(children);
  if (card) return renderCard(card);
  return <p {...props}>{children}</p>;
};

const markdownComponents = {
  h1: MarkdownH1,
  h2: MarkdownH2,
  h3: MarkdownH3,
  h4: MarkdownH4,
  h5: MarkdownH5,
  h6: MarkdownH6,
  a: MarkdownLink,
  ul: MarkdownUl,
  ol: MarkdownOl,
  li: MarkdownLi,
  p: MarkdownP,
};

const remarkPlugins = [remarkGfm];

export const AskAIMessageMarkdown = ({ children }: { children: string }) => {
  const normalized = useMemo(
    () => normalizeAskAIMarkdown(children),
    [children],
  );
  return (
    <ReactMarkdown
      remarkPlugins={remarkPlugins}
      components={markdownComponents}
    >
      {normalized}
    </ReactMarkdown>
  );
};

export default AskAIMessageMarkdown;
